
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next/types';
import { Makeswift, Page as MakeswiftPage, PageProps as MakeswiftPageProps } from '@makeswift/runtime/next';
import { SWRConfig } from 'swr';
import { BlogPostFeedDocument, PersonsDocument } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { pageLocales } from '@/utils/locales/page-locales';
import { DEFAULT_LIMIT } from '@/components/Contentful/entries/Person/PersonBlogPostFeed';
import { PersonMeta } from '@/components/Contentful/entries/Person/PersonMeta';
import { PreviewToast } from '@/components/PreviewToast';
type Props = {
    fallback: {
        [key: string]: unknown;
    };
    preview: boolean;
} & MakeswiftPageProps;
export default function Page({ snapshot, fallback, preview }: Props) {
    return (<SWRConfig value={{ fallback }}>
			<PreviewToast preview={preview}/>
			<PersonMeta>
				<MakeswiftPage snapshot={snapshot}/>
			</PersonMeta>
		</SWRConfig>);
}
async function getStaticProps({ params, previewData, preview, locale }: GetStaticPropsContext<{
    slug: string;
}, {
    makeswift: boolean;
}>) {
    if (!pageLocales.blog?.includes(locale as Locale)) {
        console.warn('Locale not supported for /blog/authors/[slug]');
        return { notFound: true, revalidate: 3600 };
    }
    const client = contentful({ preview: Boolean(preview) });
    const isMakeSwiftPreview = previewData?.makeswift == true;
    const slug = params?.slug;
    if (slug == null)
        throw new Error('"slug" URL parameter must be defined.');
    const author = await client.request(PersonsDocument, {
        where: { slug },
        locale,
        preview: Boolean(preview)
    });
    // 404 if no author is found for a particular locale
    if (author?.personCollection?.items &&
        author?.personCollection?.items?.length <= 0 &&
        !isMakeSwiftPreview) {
        console.warn(`No author found for slug ${slug} and locale ${locale}`);
        return { notFound: true, revalidate: 3600 };
    }
    const snapshot = await getPageSnapshot({
        pathname: '/blog/author/__template__',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/blog/author/__template__" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        console.warn('Could not get snapshot for /blog/author/__template__');
        return { notFound: true, revalidate: 3600 };
    }
    return {
        props: {
            snapshot,
            fallback: {
                [serializeCacheKey('person', { slug, locale })]: author,
                [serializeCacheKey('blogPostFeed', {
                    authorSlug: slug,
                    limit: DEFAULT_LIMIT,
                    skip: 0,
                    locale,
                    preview: Boolean(preview)
                })]: await client.request(BlogPostFeedDocument, {
                    where: { author: { slug }, status: true, hideOnHomepage: false },
                    limit: DEFAULT_LIMIT,
                    skip: 0,
                    locale,
                    preview: Boolean(preview)
                })
            },
            previewMode: Makeswift.getPreviewMode(previewData),
            preview: Boolean(preview),
            pageId: author?.personCollection?.items?.[0]?.sys?.id ?? null
        },
        revalidate: 3600
    };
}
export async function getStaticPaths() {
    if (Boolean(process.env.SKIP_BUILD_STATIC_GENERATION)) {
        return {
            paths: [],
            fallback: 'blocking'
        };
    }
    const { personCollection } = await contentful().request(PersonsDocument, {
        limit: 5,
        where: {
            type_not_in: ['Leader', 'Advisor'],
            bio_exists: true,
            avatar_exists: true,
            slug_exists: true
        },
        locale: 'en-US'
    });
    return {
        paths: Array.from(new Set([...(personCollection?.items ?? []).map((entry) => entry?.slug)]))
            .filter(Boolean)
            .map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    };
}

    async function __Next_Translate__getStaticProps__1961751eef4__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/blog/author/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1961751eef4__ as getStaticProps }
  